/**
 * Harberger Tax Utilities
 *
 * Contract formula:
 *   taxOwed = price × taxRateBps × elapsed / (ONE_WEEK × BPS)
 *
 * The indexer only updates seat.balance on discrete events (deposit, withdraw,
 * buyout …). Between events the balance displayed in the UI must be reduced
 * by the tax that has silently accrued since the last on-chain action.
 */

import { safeBigInt } from '@/lib/bigint';

export const ONE_WEEK_S = 604800n; // seconds
export const BPS = 10_000n;
export const DEFAULT_TAX_RATE_BPS = 500n; // 5 % per week

/**
 * Compute the tax-adjusted (real) balance by subtracting tax accrued since
 * the last on-chain update.
 *
 * @param balance        Raw balance from the indexer / RPC (USDC, 6 decimals)
 * @param price          Self-assessed price (USDC, 6 decimals)
 * @param taxRateBps     Tax rate in basis points (e.g. 500 = 5 %/week)
 * @param lastUpdateTime Unix seconds of the last on-chain balance change
 * @param nowSeconds     Current time in unix seconds (defaults to Date.now()/1000)
 */
export function taxAdjustedBalance(
  balance: bigint,
  price: bigint,
  taxRateBps: bigint = DEFAULT_TAX_RATE_BPS,
  lastUpdateTime: number = 0,
  nowSeconds?: number,
): bigint {
  if (balance <= 0n || price <= 0n || taxRateBps <= 0n) return balance;
  const now = BigInt(nowSeconds ?? Math.floor(Date.now() / 1000));
  const elapsed = now - BigInt(lastUpdateTime);
  if (elapsed <= 0n) return balance;
  const taxOwed = (price * taxRateBps * elapsed) / (ONE_WEEK_S * BPS);
  return balance > taxOwed ? balance - taxOwed : 0n;
}

/**
 * Weekly tax amount for a given price and rate.
 */
export function weeklyTax(price: bigint, taxRateBps: bigint = DEFAULT_TAX_RATE_BPS): bigint {
  return (price * taxRateBps) / BPS;
}

/**
 * Runway in seconds until balance is fully consumed by tax.
 *
 * Derived from the contract: elapsed = balance × ONE_WEEK × BPS / (price × taxRateBps)
 */
export function runwaySeconds(
  balance: bigint,
  price: bigint,
  taxRateBps: bigint = DEFAULT_TAX_RATE_BPS,
): number {
  if (price <= 0n || taxRateBps <= 0n || balance <= 0n) return 0;
  const wTax = weeklyTax(price, taxRateBps);
  if (wTax <= 0n) return 0;
  return Number((balance * ONE_WEEK_S) / wTax);
}

/**
 * Runway in fractional weeks (convenience wrapper around runwaySeconds).
 */
export function runwayWeeks(
  balance: bigint,
  price: bigint,
  taxRateBps: bigint = DEFAULT_TAX_RATE_BPS,
): number {
  return runwaySeconds(balance, price, taxRateBps) / Number(ONE_WEEK_S);
}

export interface RpcSeatMetrics {
  currentBalance?: string;
  timeUntilForfeiture?: number;
}

/**
 * Resolve balance and runway, preferring authoritative RPC metrics when
 * available and falling back to client-side tax deduction otherwise.
 *
 * Centralises the "prefer on-chain truth" pattern so every page that
 * displays duration uses the same logic.
 */
export function resolveBalanceAndRunway(
  rawBalance: bigint,
  price: bigint,
  taxRateBps: bigint,
  lastUpdateTime: number,
  metrics?: RpcSeatMetrics | null,
): { balance: bigint; runwaySeconds: number } {
  const MAX_REASONABLE_RUNWAY_S = 100 * 365 * 24 * 3600; // 100 years

  const hasRpc = metrics?.timeUntilForfeiture !== undefined;

  // V2 grid seats get placeholder metrics (currentBalance "0",
  // timeUntilForfeiture 0) even though the seat has a real deposit.
  // Detect this and fall back to client-side calculation.
  const isPlaceholder = hasRpc
    && metrics!.timeUntilForfeiture === 0
    && (!metrics!.currentBalance || metrics!.currentBalance === "0")
    && rawBalance > 0n;

  const useRpc = hasRpc && !isPlaceholder;

  // `safeBigInt` (not bare `BigInt`) — `resolveBalanceAndRunway` is on the
  // mobile-chrome render path via `transformToSeatPosition` →
  // `useLobbySeatHoldings` → `MobileBoardRail`, which sits *outside* the
  // route-group `error.tsx`. A malformed RPC `currentBalance` string would
  // otherwise throw synchronously and surface as Next.js's bare
  // "Application error" fallback. Falling back to `undefined` here lets
  // the `??` operator route to the client-side `taxAdjustedBalance`
  // computation — same behaviour as if RPC metrics were missing.
  const rpcBalanceRaw = useRpc && metrics!.currentBalance
    ? safeBigInt(metrics!.currentBalance, -1n)
    : -1n;
  const rpcBalance = rpcBalanceRaw >= 0n ? rpcBalanceRaw : undefined;

  const balance = rpcBalance
    ?? taxAdjustedBalance(rawBalance, price, taxRateBps, lastUpdateTime);

  // Cap absurd values — contract returns uint256.max for zero-price seats
  // which Number() casts to ~1.16e77, producing "1.9e17 weeks" in the UI.
  const runway = useRpc && metrics!.timeUntilForfeiture! <= MAX_REASONABLE_RUNWAY_S
    ? metrics!.timeUntilForfeiture!
    : runwaySeconds(balance, price, taxRateBps);

  return { balance, runwaySeconds: runway };
}
